import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { createResource, useResource } from '@/lib/i18n'
import { NextPage } from 'next'
import Link from 'next/link'
import Image from 'next/image'
import { eventLogProducer } from '@/lib/event-logger'
import { Action } from '@@/action_pb'
import { getABTestResult, shimmer, toBase64 } from '@/lib/utils'
import { useWindowSize } from '@/lib/use-hooks/use-window-size'
import { useCurrentLanguage } from '@/lib/current-language'
import Calculation from './calculation'
import LazyLoad from 'react-lazyload'
import { playPath } from '@/lib/utils'
import { IDownloadType, ILocation } from '@/lib/enums/device'
import IosDownloadBtn from '../common/ios-download-btn'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'

const SAFETY_TIPS_PATH = '/education/fraud-adalah'

const resource = createResource({
  en: {
    fiveMinutes: 'Only Need 5 Minutes',
    applyNow: 'Apply now',
    apkText: 'Scan To Download APK',
    mainTitle: 'Get fast online loan, just in 5 minutes on Kredit Pintar',
    caution:
      "Be cautious of potential scams and always use Kredit Pintar's official channels for transactions. Verify all information, repayment details, and terms carefully.",
    riskNotice:
      "PLEASE BE AWARE, THIS TRANSACTION CARRIES A HIGH RISK. YOU MAY EXPERIENCE LOSSES OR LOSE MONEY. DON'T TAKE LOAN IF YOU DON'T HAVE ABILITY TO REPAY. CONSIDER THIS WISELY BEFORE TRANSACTION.",
    safetyTips: 'Learn Safety Tips',
  },
  id: {
    fiveMinutes: 'CUKUP 5 MENIT',
    applyNow: 'Ajukan sekarang',
    apkText: 'Scan untuk Download APK',
    mainTitle: 'Dapatkan Pinjaman Online Cepat Cair Cukup 5 Menit di Kredit Pintar',
    caution:
      'Waspadai penipuan dan selalu gunakan platform resmi untuk bertransaksi. Pastikan ulang semua informasi yang ada berikut juga pada detail pembayaran.',
    riskNotice:
      'HATI-HATI, TRANSAKSI INI BERISIKO TINGGI. ANDA DAPAT SAJA MENGALAMI KERUGIAN ATAU KEHILANGAN UANG. JANGAN BERUTANG JIKA TIDAK MEMILIKI KEMAMPUAN MEMBAYAR. PERTIMBANGKAN SECARA BIJAK SEBELUM BERTRANSAKSI.',
    safetyTips: 'Pelajari Tips Keamanan',
  },
})

type Params = {
  isMobile: boolean
}

const Banner: NextPage<Params> = (params: Params) => {
  const { t } = useResource(resource)
  const { language: lng } = useCurrentLanguage()
  const { width } = useWindowSize()
  const isMobile = width < 1000
  const isFromMobile = width > 0 ? width < 600 : params.isMobile
  // Must match SSR for first paint; resolve A/B after mount (getABTestResult uses localStorage).
  const [branch, setBranch] = useState<'A' | 'B'>('A')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const b = getABTestResult()
    setBranch(b)
    eventLogProducer.push({
      action: Action.HOMECONTENTLOADRESULT,
      extra: { branch: b },
    })

    const isFirstVisit = !sessionStorage.getItem('hasVisited')
    if (isFirstVisit) {
      setOpen(true)
      sessionStorage.setItem('hasVisited', 'true')
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const timer = setTimeout(() => setOpen(false), 60000)
    return () => clearTimeout(timer)
  }, [open])

  const handleClose = () => {
    setOpen(false)
  }

  const goToDown = (herf: string) => {
    eventLogProducer.push({
      action: Action.GETITONAPPGALLERYBUTTONCLICK,
    })
    setTimeout(() => {
      window.open(herf)
    }, 1000)
  }

  const eventClick = () => {
    eventLogProducer.push({
      action: Action.GETITONGOOGLEPLAYBUTTONCLICK,
    })
    goToDown(playPath(IDownloadType.GOOGLE_PLAY, '', ILocation.HOME))
  }
  return (
    <BannerBox showSubTitle={branch === 'A'} innerWidth={width}>
      <div className="container">
        <div className="left">
          <section className="brands">
            <div className="ojk">
              <Image
                src={'/images/home/ojk-logo-new-25.png'}
                width={isFromMobile ? 72 : 161}
                height={isFromMobile ? 19 : 44}
                quality={85}
                priority
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(isFromMobile ? 87 : 161, isFromMobile ? 24 : 44),
                )}`}
                alt="ojk"
              />
            </div>
            <div className="afpi">
              <Image
                src={'/images/home/afpi-new.png'}
                width={isFromMobile ? 59 : 109}
                height={isFromMobile ? 24 : 44}
                quality={85}
                priority
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(isFromMobile ? 59 : 109, isFromMobile ? 24 : 44),
                )}`}
                alt="afpi"
              />
            </div>
            <div className="brand-logo">
              <Image
                src={'/images/home/top-brand.png'}
                width={isFromMobile ? 24 : 48}
                height={isFromMobile ? 24 : 48}
                quality={85}
                priority
                placeholder="empty"
                alt="brand-logo"
              />
            </div>
            <div className="brand-logo">
              <Image
                src={'/images/home/bsi-iso.png'}
                width={isFromMobile ? 48 : 59}
                height={isFromMobile ? 24 : 30}
                quality={85}
                priority
                placeholder="empty"
                alt="brand-logo"
              />
            </div>
            <div className="brand-logo">
              <Image
                src={'/images/home/laps-sjk-logo.png'}
                width={isFromMobile ? 48 : 59}
                height={isFromMobile ? 24 : 30}
                quality={85}
                priority
                placeholder="empty"
                alt="brand-logo"
              />
            </div>
          </section>
          <h1 className="hide-title">{t('mainTitle')}</h1>
          <div className="subtitle">
            {lng === 'en' ? (
              <span>
                Get
                <span className="line-title"> Fast, Low Interest Loans </span>
                from Kredit Pintar
              </span>
            ) : (
              <span>
                Dapatkan Pencairan
                <span className="line-title"> Pinjaman Dana Cepat </span>
                dengan Bunga Rendah
              </span>
            )}
          </div>
          {branch === 'A' ? (
            <h2 className="des">
              {lng === 'id' ? (
                <span>
                  Pinjam uang cepat hingga
                  <span className="green"> 50 Juta </span>
                  tanpa jaminan
                </span>
              ) : (
                <span>
                  Receive up to
                  <span className="green"> IDR 50,000,000 </span>
                  disbursement
                </span>
              )}
            </h2>
          ) : (
            <div></div>
          )}

          <LazyLoad>
            <div className="google-play">
              <section className="banner goto" onClick={eventClick}>
                <img className="qrcode" src="/images/qrcode/qr_home_gp.png" alt="qrcode" />
                <Image
                  src="/images/home/google_play.png"
                  alt="google_play"
                  width={217}
                  height={66}
                  quality={85}
                  placeholder="blur"
                  blurDataURL={`data:image/svg+xml;base64,${toBase64(shimmer(217, 66))}`}
                />
              </section>
              <div className="self-down">
                <IosDownloadBtn location={ILocation.HOME} isMobile={isMobile} />
              </div>
            </div>
            <div className="google-play">
              <div className="app_gallery" onClick={() => goToDown(playPath(IDownloadType.HUAWEI))}>
                <section className="banner goto">
                  <Image
                    src="/images/home/app_gallery.png"
                    alt="app_play"
                    width={217}
                    height={66}
                    quality={85}
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(shimmer(217, 66))}`}
                  />
                </section>
              </div>
              <div className="app_gallery">
                <section className="banner self-down">
                  <img className="qrcode" src="/images/qrcode/qr_apk.png" alt="qrcode" />
                  <span className="apk_text">{t('apkText')}</span>
                  <Image
                    src="/images/qrcode/qr_apk_bg.png"
                    alt="qr_apk_bg"
                    width={217}
                    height={66}
                    quality={85}
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(shimmer(217, 66))}`}
                  />
                </section>
              </div>
            </div>
          </LazyLoad>
        </div>
        <div className="right">
          <Calculation />
        </div>
        <WarningDialog open={open} keepMounted onClose={handleClose} aria-labelledby="ojk-warning-title">
          <DialogContent className="warning-content" onClick={handleClose}>
            <div className="warning-content-left">
              <Image src="/images/home/icon_caution.svg" alt="" width={20} height={20} />
              <div className="warning-text-container">
                <div className="warning-text">
                  <p id="ojk-warning-title" className="warning-caution">
                    {t('caution')}
                  </p>
                  <p className="warning-notice">{t('riskNotice')}</p>
                </div>

                <Link href={SAFETY_TIPS_PATH} className="warning-link" onClick={(event) => event.stopPropagation()}>
                  {t('safetyTips')}
                  <Image src="/images/home/icon_arrow_right.svg" alt="" width={16} height={16} />
                </Link>
              </div>
            </div>
            <button
              type="button"
              className="warning-close"
              aria-label="close"
              onClick={(event) => {
                event.stopPropagation()
                handleClose()
              }}
            >
              <Image src="/images/home/icon_close.svg" alt="" width={12} height={12} />
            </button>
          </DialogContent>
        </WarningDialog>
      </div>
    </BannerBox>
  )
}

export default Banner

const WarningDialog = styled(Dialog)`
  .MuiDialog-paper {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    margin: 0;
    overflow-x: hidden;
    background: #eaf7ef;
    border-top: 1px solid #1cc812;
    box-shadow: none;
    border-radius: 0;
  }
  .warning-close {
    position: absolute;
    right: 56px;
    top: 16px;
    width: 24px;
    height: 24px;
    background: #F5F5F5;
    border-radius: 50%;
  }
  .warning-content {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin: 12px auto;
    padding: 0;
    max-width: 1040px;
  }
  .warning-content-left {
    display: flex;
    align-items: flex-start;
    flex: 1;
    gap: 12px;
  }
  .warning-text-container {
    display: flex;
    gap: 8px;
  }
  .warning-text {
    flex: 1;
    min-width: 0;
  }
  .warning-caution {
    margin: 0 0 4px;
    font-size: 14px;
    line-height: 18px;
    color: #101010;
    font-family: Roboto-Bold, Roboto;
  }
  .warning-notice {
    margin: 0;
    font-size: 14px;
    line-height: 15px;
    color: #666666;
    font-family: Roboto-Regular, Roboto;
  }
  .warning-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 800;
    color: #1cc812;
    text-decoration: none;
    height: 24px;
    font-family: Roboto-Bold, Roboto;
  }
  @media only screen and (max-width: 1000px) {
    .MuiDialog-paper {
      border-top-right-radius: 8px;
      border-top-left-radius: 8px;
    }
    .warning-close {
      right: 16px;
      top: 20px;
    }
    .warning-content {
      padding: 20px 16px;
      margin: 0 36px 0 0;
      max-width: unset;
    }
    .warning-text-container {
      flex-direction: column;
      gap: 12px;
    }
    .warning-content-left {
      margin-right: 0;
      gap: 8px;
    }
  }
`

type IBannerBox = {
  showSubTitle: boolean
  innerWidth: number
}

const BannerBox = styled.div<IBannerBox>`
  .hide-title {
    position: absolute;
    top: 0;
    opacity: 0;
    z-index: -1;
  }
  @media only screen and (max-width: 1000px) {
    .container {
      height: ${(props) =>
        `${
          props.showSubTitle
            ? `${(props.innerWidth * (432 / 320)).toFixed()}px`
            : `${(props.innerWidth * (376 / 320)).toFixed()}px`
        }`};
      background: #eff0f0;
      position: relative;
      margin: 0 auto 110px;
      padding: 0 24px !important;
      .left {
        padding-top: 17px !important;
        width: 100% !important;
        .brands {
          display: flex;
          align-items: center;
          .lazyload-wrapper {
            display: flex;
          }
          .ojk {
            width: 20%;
            margin-right: 20px;
          }
          .afpi {
            width: 20%;
          }
          .brand-logo {
            width: 20%;
            text-align: center;
          }
        }
        .line-title {
          display: inline;
          font-size: 20px;
        }
        .subtitle {
          font-size: 16px;
          font-family: Roboto-Regular, Roboto;
          font-weight: 400;
          color: rgba(0, 0, 0, 0.87);
          line-height: 20px;
        }
        .des {
          width: 240px;
          font-size: 12px;
          font-family: Roboto-Regular, Roboto;
          font-weight: 400;
          margin-top: 16px;
          color: #9e9e9e;
          line-height: 16px;
        }
        .green {
          color: #1cc812;
        }
        .google-play {
          display: none;
        }
        .app_gallery {
          display: none;
        }
        .apply-btn {
          position: absolute;
          bottom: -72px;
          left: 50%;
          transform: translateX(-50%);
          width: 208px;
          line-height: 40px;
          height: 40px;
          background: #1cc812;
          border-radius: 8px;
          text-align: center;
          font-size: 14px;
          font-family: Roboto-Medium, Roboto;
          font-weight: 500;
          color: #ffffff;
          z-index: 99;
        }
      }
      .hide-close {
        display: none !important;
      }
    }
  }
  @media only screen and (min-width: 1000px) {
    .container {
      height: 612px;
      width: 100%;
      max-width: 1440px;
      margin: 0 auto;
      display: flex;
      justify-content: space-between;
      padding: 0 150px;
      background-image: url('/images/home/banner-b.png');
      background-size: 100% 100%;
      background-repeat: no-repeat;
      margin-bottom: 80px;
      .left {
        padding-top: 72px;
        .brands {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 450px;
          .brand-logo {
            width: 64px;
          }
        }
        .line-title {
          display: inline;
          font-size: 40px;
          font-family: Roboto-Bold, Roboto;
          font-weight: 500;
        }
        .subtitle {
          font-size: 32px;
          font-family: Roboto-Regular, Roboto;
          font-weight: 400;
          color: rgba(0, 0, 0, 0.87);
          line-height: 36px;
          margin-top: 54px;
          margin-right: 24px;
        }
        .des {
          width: 520px;
          font-size: 20px;
          font-family: Roboto-Regular, Roboto;
          font-weight: 400;
          margin-top: 30px;
          color: #9e9e9e;
          line-height: 28px;
        }
        .green {
          color: #1cc812;
        }
        .app_gallery {
          margin-top: 16px;
          width: 217px;
        }
        .google-play {
          display: flex;
          &:first-child {
            margin-top: 50px;
          }
          .goto {
            cursor: pointer;
          }
          .banner {
            position: relative;
            width: 217px;
            height: 66px;
            box-sizing: border-box;
            .qrcode {
              position: absolute;
              top: 50%;
              right: 0;
              transform: translateY(-50%);
              width: 100%;
              height: 100%;
              z-index: 1;
              opacity: 0;
            }
          }
          .banner:hover .qrcode {
            top: 140px;
            width: 160px;
            height: 160px;
            border-radius: 8px;
            background: #fff;
            box-shadow: 0px 6px 12px 0px rgba(0, 0, 0, 0.1);
            border: 1px solid #f5f5f5;
            opacity: 1;
            z-index: 3;
          }
          .self-down {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: 10px;
            .apk_text {
              position: absolute;
              font-size: 16px;
              font-family: Roboto-Medium, Roboto;
              font-weight: 500;
              color: #fff;
              letter-spacing: -0.6px;
              line-height: 19px;
              width: 58%;
              padding-left: 8px;
              z-index: 1;
            }
          }
        }
        .apply-btn {
          display: none;
        }
      }
      .right {
        margin: 6% 0 0 0;
      }
    }
  }
`
